import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Zap, Repeat, ShieldCheck, Gauge, MessagesSquare, Layers, FileUp, Search, FileCheck2, Banknote, Building2, Home as HomeIcon, Landmark, Hammer, Briefcase, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROGRAMS, PROGRAM_ORDER } from "@/data/site";
import { ProgramCard } from "@/components/site/ProgramCard";

const HERO_IMG = "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function Reveal({ children, delay = 0, className = "" }) {
  return <div className="animate-fade-up" style={{ animationDelay: `${delay}ms` }}>{className ? <div className={className}>{children}</div> : children}</div>;
}

const programIcons = {
  "fix-and-flip": Hammer, "dscr-rental": HomeIcon, bridge: TrendingUp,
  "ground-up-construction": Building2, multifamily: Landmark, portfolio: Briefcase,
};

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="bg-obsidian">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-3xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-brandred animate-pulse-red" />
                <span className="eyebrow">Capital for Real Estate Investors</span>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl text-white">
                You found the deal.<br />
                <span className="gold-text italic">We fund it.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-base sm:text-lg text-dim max-w-xl leading-relaxed">
                Financing solutions for acquisitions, rentals, renovations, construction, earnest money, and
                transactional closings.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate("/apply")} data-testid="hero-submit-deal-cta" className="btn-gold rounded-full h-14 px-8 text-base gap-2">
                  Apply for Financing <ArrowRight className="w-5 h-5" />
                </Button>
                <Button variant="outline" onClick={() => navigate("/loan-programs")} data-testid="hero-view-programs-cta" className="btn-outline-glass rounded-full h-14 px-8 text-base">
                  View Loan Programs
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="loan-programs-section">
        <div className="max-w-2xl">
          <div className="eyebrow mb-3">Loan Programs</div>
          <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl text-white">Financing built around the <span className="gold-text italic">deal.</span></h2>
          <p className="mt-4 text-dim">Eight financing lines engineered for the way investors actually transact — from the first offer to the final wire.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROGRAM_ORDER.map((slug) => (
            <ProgramCard key={slug} slug={slug} title={PROGRAMS[slug].title} desc={PROGRAMS[slug].short} Icon={programIcons[slug] || Layers} />
          ))}
          <ProgramCard slug="emd-funding" eyebrow="Need EMD?" title="Earnest Money Deposit Funding" desc="Short-term earnest money funding for qualified investment transactions." Icon={Zap} accent="gold" fundLabel="Get EMD Funding" onFund={() => navigate("/emd-funding")} />
          <ProgramCard slug="transactional-funding" eyebrow="Need a Double Close Funded?" title="Transactional Funding" desc="Short-duration transactional capital for qualified A-B / B-C closings." Icon={Repeat} accent="red" fundLabel="Get Transactional Funding" onFund={() => navigate("/transactional-funding")} />
        </div>
      </section>

      {/* WHY BULLSEYE */}
      <section className="relative py-20 lg:py-28 border-y border-edge grain-bg" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="eyebrow mb-3">Why BullsEye Capital</div>
          <h2 className="h-display text-3xl sm:text-4xl text-white max-w-2xl">Built for operators who move <span className="gold-text italic">fast.</span></h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, t: "Investor-Focused", d: "We finance business-purpose real estate — underwritten around the deal, not consumer boxes." },
              { icon: Layers, t: "Multiple Financing Solutions", d: "Access financing across eight investment strategies from a single point of contact." },
              { icon: Gauge, t: "Simple Application", d: "A clean, practical application that respects your time. No unnecessary questions." },
              { icon: MessagesSquare, t: "Fast Communication", d: "Direct communication and clear next steps so you always know where your deal stands." },
            ].map((f, i) => (
              <div key={i} className="group relative rounded-2xl p-6 bg-charcoal border border-edge overflow-hidden transition-all duration-300 hover:border-gold/50 hover:-translate-y-1" data-testid={`why-card-${i}`}>
                <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-gold/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 grid place-items-center">
                  <f.icon className="w-6 h-6 text-gold" strokeWidth={1.7} />
                </div>
                <h3 className="mt-5 text-lg font-bold text-white">{f.t}</h3>
                <p className="mt-2 text-sm text-dim leading-relaxed">{f.d}</p>
                <div className="mt-5 h-px w-10 bg-gold/40 group-hover:w-16 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="how-it-works-section">
        <div className="eyebrow mb-3">How It Works</div>
        <h2 className="h-display text-3xl sm:text-4xl text-white max-w-2xl">From submission to <span className="gold-text italic">funding.</span></h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { n: "01", t: "Submit the Deal", d: "Tell us about the property and your financing needs.", icon: FileUp },
            { n: "02", t: "Upload Documents", d: "Securely provide contracts and supporting documents.", icon: Layers },
            { n: "03", t: "BullsEye Reviews It", d: "Our team reviews the transaction and structure.", icon: Search },
            { n: "04", t: "Review Financing", d: "We share available financing options for your deal.", icon: FileCheck2 },
            { n: "05", t: "Close", d: "Move to closing with clear coordination.", icon: Banknote },
          ].map((s, i) => (
            <div key={i} className="group relative rounded-2xl p-6 bg-charcoal border border-edge overflow-hidden transition-all duration-300 hover:border-gold/50" data-testid={`step-${i + 1}`}>
              <span className="absolute top-3 right-4 h-display text-5xl text-gold/10 group-hover:text-gold/25 transition-colors">{s.n}</span>
              <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 grid place-items-center">
                <s.icon className="w-5 h-5 text-gold" strokeWidth={1.7} />
              </div>
              <h3 className="mt-4 text-base font-bold text-white">{s.t}</h3>
              <p className="mt-1.5 text-sm text-dim leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ARCHER — BRAND FIGURE */}
      <section className="relative overflow-hidden border-y border-edge bg-charcoal/40 grain-bg" data-testid="archer-section">
        <div className="absolute -left-24 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="absolute right-0 bottom-0 w-[420px] h-[420px] rounded-full bg-brandred/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <div className="eyebrow mb-3">Meet Archer</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl text-white">The face of <span className="gold-text italic">precision.</span></h2>
            <p className="mt-4 text-dim leading-relaxed max-w-xl">
              Archer is the spirit of BullsEye Capital — sharp-eyed, disciplined, and built for the deal. Every
              transaction gets the same treatment: lock the target, move fast, and execute with precision.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Sharp-eyed review on every transaction",
                "Technology-assisted workflows that keep files moving",
                "Direct, no-runaround communication",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-gold shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate("/apply")} className="btn-gold rounded-full h-13 px-8 mt-8 gap-2 h-14" data-testid="archer-cta">
              Apply for Financing <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="order-1 lg:order-2 relative flex justify-center">
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-[70%] aspect-square rounded-full border border-gold/20" />
            </div>
            <img src="/archer.png" alt="Archer — BullsEye Capital" data-testid="archer-image"
              className="relative z-10 max-h-[440px] lg:max-h-[540px] w-auto animate-fade-up drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]" />
          </div>
        </div>
      </section>

      {/* CAPITAL PARTNERS + TECH */}
      <section className="border-y border-edge bg-charcoal/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div data-testid="home-partners-block">
            <div className="eyebrow mb-3">Capital Partners</div>
            <h2 className="h-display text-3xl sm:text-4xl text-white">Multiple capital sources. One point of contact.</h2>
            <p className="mt-4 text-dim leading-relaxed">
              BullsEye Capital works with lending and capital partners that serve real estate investors across
              multiple strategies and markets — so you spend less time shopping and more time closing.
            </p>
            <Button variant="outline" onClick={() => navigate("/partners")} className="btn-outline-glass rounded-full mt-6 h-12 px-6" data-testid="home-partners-cta">
              Explore Capital Partners
            </Button>
          </div>
          <div className="card-rich p-8" data-testid="home-tech-block">
            <div className="eyebrow mb-3">Technology-Enabled Precision</div>
            <h3 className="h-display text-2xl sm:text-3xl text-white">Powered by BullsEye technology.</h3>
            <p className="mt-4 text-dim leading-relaxed">
              BullsEye Capital uses technology-assisted workflows to organize transactions, collect documents,
              and streamline financing requests — keeping every deal moving.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24 lg:py-32" data-testid="final-cta-section">
        <div className="absolute inset-0 grain-bg opacity-60" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/10 blur-[120px]" />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <h2 className="h-display text-4xl sm:text-6xl text-white">Have a deal?<br /><span className="gold-text italic">Submit it.</span></h2>
          <p className="mt-5 text-dim">You found the deal. We help fund it. Submit your transaction and a BullsEye Capital representative will follow up.</p>
          <Button onClick={() => navigate("/apply")} data-testid="final-submit-deal-cta" className="btn-gold rounded-full h-14 px-10 text-base mt-8 gap-2">
            Apply for Financing <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}

function FastCard({ testId, icon: Icon, eyebrow, title, desc, cta, onClick, accent, archer }) {
  const ring = accent === "red" ? "hover:border-brandred/60" : "hover:border-gold/60";
  const iconBg = accent === "red" ? "bg-brandred/15 text-brandred" : "bg-gold/15 text-gold";
  return (
    <div className={`relative overflow-hidden card-surface rounded-2xl p-6 sm:p-8 min-h-[280px] ${ring}`} data-testid={testId}>
      {archer && (
        <img src={archer} alt="" aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 h-[104%] max-w-[40%] w-auto object-contain object-right-bottom drop-shadow-[0_16px_30px_rgba(0,0,0,0.6)]" />
      )}
      <div className="relative z-10 max-w-[60%]">
        <span className="eyebrow">{eyebrow}</span>
        <div className="flex items-center gap-3 mt-3">
          <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${iconBg}`}><Icon className="w-5 h-5" /></div>
          <h3 className="text-lg sm:text-2xl font-bold text-white leading-tight">{title}</h3>
        </div>
        <p className="mt-3 text-sm text-dim leading-relaxed">{desc}</p>
        <Button onClick={onClick} className={`mt-5 rounded-full h-12 px-6 gap-2 w-full sm:w-auto ${accent === "red" ? "bg-brandred hover:bg-brandred/90 text-white font-bold tracking-normal" : "btn-gold"}`} data-testid={`${testId}-cta`}>
          {cta} <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function FundProductCard({ slug, icon: Icon, title, desc, onLearn, onFund, fundLabel }) {
  return (
    <div className="card-surface rounded-2xl p-7 flex flex-col border-gold/30" data-testid={`program-card-${slug}`}>
      <Icon className="w-8 h-8 text-gold" />
      <h3 className="mt-5 text-xl font-bold text-white">{title}</h3>
      <p className="mt-2 text-sm text-dim leading-relaxed flex-1">{desc}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={onLearn} className="text-sm font-semibold text-white/70 hover:text-gold">Learn More</button>
        <Button onClick={onFund} size="sm" className="btn-gold rounded-full h-9 px-4 ml-auto" data-testid={`program-card-${slug}-fund`}>{fundLabel}</Button>
      </div>
    </div>
  );
}
